/**
 * Beat 1's taken littermate, awake (D-250). Pure simulation: no Three, no
 * Rapier, stepped on the fixed step so every replay of the beat matches.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * Until session 91 both littermates slept through the whole beat, and the one
 * that was taken was a prop the player had never touched. Alex's report: the
 * piglets "just lie around", and the taking "is just disappearing". The
 * tutorial bonded the player with her mother and then took somebody else.
 *
 * So the one who will be taken is the one who wakes. Rosalie nudges it, it
 * squeaks and gets up, and it trots after her along the creep strip, noses the
 * straw when she stops, and goes back under the lamp when something moves at
 * the door. Then the boots come for it.
 *
 * ── Why one axis ─────────────────────────────────────────────────────────────
 *
 * The creep strip is 2.2 m along X and 0.51 m across Z — narrower than two
 * piglets side by side. Nothing can pass anything in it, so "following" here is
 * keeping a nose-to-tail gap on whichever side of her it is already on, and a
 * wander in Z inside a few centimetres so it does not read as a bead on a wire.
 * No pathfinding, no avoidance: the geometry does not allow a second route.
 */

/** A ground-plane point. */
export interface SiblingPoint {
  readonly x: number;
  readonly z: number;
}

/**
 * `asleep` → `rising` → `following` ⇄ `returning` → `lying` → `resting`.
 *
 * There is no way back to `asleep`: once it has woken it stays a presence the
 * player knows, and what the boots take is a piglet that was awake with her.
 */
export type SiblingPhase = 'asleep' | 'rising' | 'following' | 'returning' | 'lying' | 'resting';

export interface SiblingSnapshot {
  readonly phase: SiblingPhase;
  readonly x: number;
  readonly z: number;
  /** Radians: 0 faces +Z, increasing toward +X (her `facing` convention). */
  readonly yaw: number;
  /** m/s along its path, for the gait. */
  readonly speed: number;
  /** 0 lying on its side, 1 standing. */
  readonly stand: number;
  /** Seconds it has stood still while awake; drives the nosing in the straw. */
  readonly stillFor: number;
  /** How many nudges it has answered. Presentation plays one squeak per step. */
  readonly answers: number;
}

export interface SiblingBounds {
  /** Where it sleeps, and the end of the strip it never goes past. */
  readonly bed: SiblingPoint & { readonly yaw: number };
  /**
   * The far limit of its range along X, toward the pen gate.
   *
   * **A containment number, not a staging one.** The gate mouth is at the
   * strip's walkway end, and `bay-staging.spec.ts` forbids anything standable
   * near it; the spec measures the standing sibling's solid at this X.
   */
  readonly xFar: number;
  /** The strip's centre line and how far off it the sibling may wander. */
  readonly zCentre: number;
  readonly zWander: number;
}

/** Nose-to-tail, centre to centre: her half-length plus its own, plus a hand. */
export const SIBLING_FOLLOW_GAP = 0.62;
/** A three-day-old piglet trots; it does not walk. Slower than her trot. */
const SPEED_MAX = 0.8;
const ACCEL = 3.2;
/** Close enough to its target to stop. Without it the gap hunts by millimetres. */
const ARRIVE = 0.06;
/** Seconds to get up off its side, and to lie back down. */
const RISE_SECONDS = 0.55;
const LIE_SECONDS = 0.9;
/** Seconds kept off its bed before it lies down where it is. */
const BLOCKED_GIVE_UP = 2.5;
/** Radians per second it turns; a piglet turns fast. */
const TURN_RATE = 5;

function approach(from: number, to: number, step: number): number {
  return from < to ? Math.min(to, from + step) : Math.max(to, from - step);
}

function wrapAngle(a: number): number {
  let r = a;
  while (r > Math.PI) r -= Math.PI * 2;
  while (r < -Math.PI) r += Math.PI * 2;
  return r;
}

function turnToward(yaw: number, target: number, step: number): number {
  const d = wrapAngle(target - yaw);
  return wrapAngle(yaw + Math.max(-step, Math.min(step, d)));
}

export class BornSibling {
  readonly #bounds: SiblingBounds;
  #phase: SiblingPhase = 'asleep';
  #x: number;
  #z: number;
  #yaw: number;
  #speed = 0;
  #stand = 0;
  #stillFor = 0;
  #answers = 0;
  #xMin: number;
  #xMax: number;

  constructor(bounds: SiblingBounds) {
    this.#bounds = bounds;
    this.#x = bounds.bed.x;
    this.#z = bounds.bed.z;
    this.#yaw = bounds.bed.yaw;
    this.#xMin = Math.min(bounds.bed.x, bounds.xFar);
    this.#xMax = Math.max(bounds.bed.x, bounds.xFar);
  }

  get snapshot(): SiblingSnapshot {
    return {
      phase: this.#phase,
      x: this.#x,
      z: this.#z,
      yaw: this.#yaw,
      speed: this.#speed,
      stand: this.#stand,
      stillFor: this.#stillFor,
      answers: this.#answers,
    };
  }

  /** True while it is up and about: the window the nudge answers in. */
  get awake(): boolean {
    return this.#phase === 'rising' || this.#phase === 'following' || this.#phase === 'returning';
  }

  /**
   * She nudged it. Wakes it the first time, and it answers every time it is
   * up. Returns true when this nudge woke it.
   *
   * It does NOT answer once it has gone back to lie down: that is the
   * rehearsal for the empty straw, where nothing answers at all.
   */
  nudge(): boolean {
    if (this.#phase === 'asleep') {
      this.#phase = 'rising';
      this.#answers += 1;
      return true;
    }
    if (this.awake) this.#answers += 1;
    return false;
  }

  /** Go back to the bed under the lamp and lie down. Idempotent; asleep stays asleep. */
  settle(): void {
    if (this.#phase === 'rising' || this.#phase === 'following') this.#phase = 'returning';
  }

  step(dt: number, her: SiblingPoint): void {
    if (!(dt > 0)) return;
    switch (this.#phase) {
      case 'asleep':
      case 'resting':
        return;
      case 'rising':
        this.#stand = Math.min(1, this.#stand + dt / RISE_SECONDS);
        this.#turnTo(her, dt);
        if (this.#stand >= 1) this.#phase = 'following';
        return;
      case 'lying':
        this.#stand = Math.max(0, this.#stand - dt / LIE_SECONDS);
        this.#yaw = turnToward(this.#yaw, this.#bounds.bed.yaw, TURN_RATE * dt);
        if (this.#stand <= 0) this.#phase = 'resting';
        return;
      case 'following':
      case 'returning':
        this.#move(dt, her);
        return;
    }
  }

  #move(dt: number, her: SiblingPoint): void {
    // Which side of her it is on decides which side it keeps. Ties (never in
    // practice: she cannot stand in it) resolve toward the bed.
    const side = this.#x - her.x > 0 ? 1 : this.#x - her.x < 0 ? -1 : Math.sign(this.#bounds.bed.x - her.x) || -1;
    // The nearest it may come to her, on its own side.
    const limit = her.x + side * SIBLING_FOLLOW_GAP;
    const wanted = this.#phase === 'returning' ? this.#bounds.bed.x : limit;
    // Never through her: the gap is a wall on its own side of her.
    const blocked = side > 0 ? Math.max(wanted, limit) : Math.min(wanted, limit);
    const target = Math.max(this.#xMin, Math.min(this.#xMax, blocked));

    const d = target - this.#x;
    const distance = Math.abs(d);
    // Inside the gap she is walking into it, and the dead band would let her
    // close another ARRIVE before it moved at all — on top of the acceleration
    // lag, that put her nose 0.53 m from its centre (born-sibling.spec). So
    // giving way has no dead band; only coming after her does.
    const inside = side > 0 ? this.#x < limit : this.#x > limit;
    const slack = inside ? 0 : ARRIVE;
    const stopping = Math.sqrt(2 * ACCEL * Math.max(0, distance - slack));
    const desired = distance <= slack ? 0 : Math.min(SPEED_MAX, stopping);
    this.#speed = approach(this.#speed, desired, ACCEL * dt);
    const dx = Math.sign(d) * Math.min(distance, this.#speed * dt);
    this.#x = Math.max(this.#xMin, Math.min(this.#xMax, this.#x + dx));

    // Z drifts toward hers, inside the strip: it is following HER, not a line.
    const zTarget = Math.max(
      this.#bounds.zCentre - this.#bounds.zWander,
      Math.min(this.#bounds.zCentre + this.#bounds.zWander, this.#phase === 'returning' ? this.#bounds.bed.z : her.z),
    );
    this.#z = approach(this.#z, zTarget, 0.25 * dt);

    if (this.#speed > 0.05) {
      this.#stillFor = 0;
      this.#yaw = turnToward(this.#yaw, Math.atan2(Math.sign(d), 0), TURN_RATE * dt);
    } else {
      this.#stillFor += dt;
      // Standing, it looks at her: attention, which D-002 allows and which is
      // the only thing that makes a piglet standing in a strip a companion.
      if (this.#phase === 'following') this.#turnTo(her, dt);
    }

    // Home, or kept from it long enough that it gives up and lies down where
    // it stands. She can stand on its bed; the beat must not wait on her to
    // move, and the lean over the kerb covers the whole of its range anyway.
    const home = Math.abs(this.#bounds.bed.x - this.#x) <= ARRIVE;
    if (this.#phase === 'returning' && this.#speed < 0.05 && (home || this.#stillFor >= BLOCKED_GIVE_UP)) {
      this.#phase = 'lying';
      this.#speed = 0;
    }
  }

  #turnTo(her: SiblingPoint, dt: number): void {
    const dx = her.x - this.#x;
    const dz = her.z - this.#z;
    if (Math.hypot(dx, dz) < 1e-3) return;
    this.#yaw = turnToward(this.#yaw, Math.atan2(dx, dz), TURN_RATE * dt);
  }
}
